import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { MagicWand, Sparkle, Gift, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACKS = (t) => [
  `### Word Scavenger Hunt\nTrace the word **${t}** five times, then find three objects in the room that relate to it.\n\n**Steps**\n1. Trace the word slowly.\n2. Say it aloud each time.\n3. Hunt for three matching objects.\n\n**Magical twist**\nEach object found earns a sparkle sticker.`,
  `### Trace & Illustrate\nTrace **${t}** in rainbow colors, then draw what the word means.\n\n**Steps**\n1. Trace in three colors.\n2. Draw a quick picture.\n3. Share one sentence with a partner.\n\n**Magical twist**\nAdd a secret detail only a close look reveals.`,
];

const MD = {
  h1: (p) => <h3 className="font-nunito font-extrabold tracking-tight text-xl text-[#17284b] mt-4 mb-1" {...p} />,
  h2: (p) => <h3 className="font-nunito font-extrabold tracking-tight text-xl text-[#17284b] mt-4 mb-1" {...p} />,
  h3: (p) => <h3 className="font-nunito font-extrabold tracking-tight text-xl text-[#17284b] mt-4 mb-1" {...p} />,
  h4: (p) => <h4 className="font-nunito font-bold text-base text-[#17284b] mt-3 mb-1" {...p} />,
  p: (p) => <p className="font-nunito text-sm sm:text-base text-[#3f4d66] leading-relaxed my-2" {...p} />,
  strong: (p) => <strong className="font-bold text-[#17284b]" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 my-2 space-y-1 font-nunito text-sm sm:text-base text-[#3f4d66]" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 my-2 space-y-1 font-nunito text-sm sm:text-base text-[#3f4d66]" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
};

export const FreeTools = () => {
  const [topic, setTopic] = useState("");
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!topic.trim()) return toast.error("Please enter a word or topic");
    setBusy(true);
    setResult("");
    try {
      const res = await axios.post(`${API}/ai/generate-lesson`, { topic });
      setResult(res.data.activity);
    } catch {
      const list = FALLBACKS(topic);
      setResult(list[Math.floor(Math.random() * list.length)]);
      toast.info("Using a pre-made activity suggestion!");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="free-tools" className="py-10 sm:py-14 px-4 bg-[#f2effb] border-y border-[#d4dff0] scroll-mt-28" aria-labelledby="free-tools-title">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-10">
          <span className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-[#7358b3] inline-block">Always free</span>
          <h2 id="free-tools-title" className="font-nunito font-extrabold tracking-tight text-3xl sm:text-4xl text-[#17284b] mt-1">Free Learning Tools</h2>
          <p className="font-nunito text-base sm:text-lg text-[#596882] mt-3">
            Try a quick AI-generated tracing activity, or grab a real worksheet sample to use today.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div id="ai-generator" className="lg:col-span-2 bg-white border border-[#d4dff0] rounded-3xl p-6 sm:p-8 scroll-mt-28" data-testid="ai-generator-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-purple-200 border border-[#d4dff0] rounded-2xl flex items-center justify-center">
                <MagicWand size={26} weight="duotone" className="text-[#17284b]" />
              </div>
              <div>
                <h3 className="font-nunito font-extrabold tracking-tight text-xl sm:text-2xl text-[#17284b]">Free AI Lesson Generator</h3>
                <p className="font-nunito text-sm text-[#596882]">Enter a word or topic → get a formatted tracing activity in seconds.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && generate()}
                placeholder="e.g. 'brave', 'prepositions', 'ocean'"
                className="flex-1 font-nunito text-base border border-[#d4dff0] rounded-xl py-6"
                data-testid="ai-topic-input"
                aria-label="Word or topic for the AI Lesson Generator"
              />
              <Button onClick={generate} disabled={busy} className="bg-[#7358b3] hover:bg-[#5f479a] text-white font-nunito font-bold border border-[#d4dff0] rounded-xl px-6 py-6" data-testid="generate-lesson-btn">
                {busy ? "Generating…" : (<><Sparkle size={20} weight="fill" className="mr-2" />Generate Activity</>)}
              </Button>
            </div>
            {result && (
              <div className="mt-5 bg-secondary/15 border-2 border-secondary rounded-2xl p-5" data-testid="ai-result">
                <p className="font-nunito font-bold text-xs uppercase tracking-wider text-slate-500">Activity for "{topic}"</p>
                <ReactMarkdown components={MD}>{result}</ReactMarkdown>
              </div>
            )}
          </div>

          <div className="bg-white border border-[#d4dff0] rounded-3xl p-6 sm:p-8 flex flex-col" data-testid="free-worksheet-card">
            <div className="w-12 h-12 bg-emerald-200 border border-[#d4dff0] rounded-2xl flex items-center justify-center mb-4">
              <Gift size={26} weight="duotone" className="text-[#17284b]" />
            </div>
            <h3 className="font-nunito font-extrabold tracking-tight text-xl sm:text-2xl text-[#17284b] mb-2">Free Context Clues Worksheet</h3>
            <p className="font-nunito text-sm sm:text-base text-[#3f4d66] leading-relaxed flex-1">
              A real tracing worksheet with answer key — perfect for a vocabulary center, morning work, or a first look at
              how the workbooks are structured.
            </p>
            <img
              src="https://ecdn.teacherspayteachers.com/thumbitem/Vocabulary-Context-Clues-Tracing-Worksheets-Synonyms-Antonyms-SPED-ESL-ELL-OT-15596502-1772484139/original-15596502-1.jpg"
              alt="Free Context Clues tracing worksheet preview"
              className="w-full rounded-xl border border-[#d4dff0] my-4 object-cover aspect-[4/3] object-top"
              loading="lazy"
            />
            <a href="#newsletter" data-testid="free-worksheet-btn">
              <Button className="w-full bg-[#ff8518] hover:bg-[#e8740f] text-white font-nunito font-bold border border-[#d4dff0] rounded-xl py-5">
                Email Me the Free Worksheet <ArrowRight size={16} weight="bold" className="ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
